import React, { useState, useRef, useEffect } from 'react';
import { getAIResponse, predefinedQuestions } from '../services/aiRules';
import './RuleBasedAI.css';

const DEFAULT_GREETING = [
  { id: 'greeting', text: "Hi there! I'm the TSIL Assistant. How can I help you today?", sender: 'ai' }
];

export default function RuleBasedAI() {
  const [isOpen, setIsOpen] = useState(false);
  
  // 1. Persistence via localStorage
  const [messages, setMessages] = useState(() => {
    const saved = localStorage.getItem('tsil_chat_messages');
    return saved ? JSON.parse(saved) : DEFAULT_GREETING;
  });
  
  const [currentQuestions, setCurrentQuestions] = useState(predefinedQuestions);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false); // represents "thinking..." state
  const [isTypewriting, setIsTypewriting] = useState(false); // represents "typing letters..." state
  
  const messagesEndRef = useRef(null);
  const typingTimerRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Auto-scroll when messages state changes or chat is opened
  useEffect(() => {
    if (isOpen) {
      scrollToBottom();
    }
  }, [messages, isOpen]);

  // Save to localStorage on change
  useEffect(() => {
    localStorage.setItem('tsil_chat_messages', JSON.stringify(messages));
  }, [messages]);

  // Cleanup typewriter on unmount
  useEffect(() => {
    return () => {
      if (typingTimerRef.current) clearInterval(typingTimerRef.current);
    };
  }, []);

  // Character-by-character typewriter loop
  const startTypewriter = (fullText, suggestedQs) => {
    setIsTyping(false);
    setIsTypewriting(true);
    
    const messageId = Date.now();
    
    // Add empty message first
    setMessages(prev => [...prev, { id: messageId, text: '', sender: 'ai' }]);
    
    let currentText = '';
    let charIndex = 0;
    
    typingTimerRef.current = setInterval(() => {
      if (charIndex < fullText.length) {
        currentText += fullText[charIndex];
        setMessages(prev => prev.map(msg => 
          msg.id === messageId ? { ...msg, text: currentText } : msg
        ));
        charIndex++;
      } else {
        clearInterval(typingTimerRef.current);
        setIsTypewriting(false);
        // Show questions once typing is fully complete
        setCurrentQuestions(suggestedQs);
      }
    }, 18); // Typing speed
  };

  const handleSend = (e) => {
    e.preventDefault();
    if (!inputValue.trim() || isTyping || isTypewriting) return;

    const userMessage = { id: Date.now() + '-user', text: inputValue, sender: 'user' };
    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsTyping(true);

    // Simulate AI thinking time
    setTimeout(() => {
      const response = getAIResponse(userMessage.text);
      startTypewriter(response.responseText, response.suggestedQuestions);
    }, 900);
  };

  const handleQuickQuestion = (question) => {
    if (isTyping || isTypewriting) return;
    
    const userMessage = { id: Date.now() + '-user', text: question, sender: 'user' };
    setMessages(prev => [...prev, userMessage]);
    setIsTyping(true);
    
    setTimeout(() => {
      const response = getAIResponse(question);
      startTypewriter(response.responseText, response.suggestedQuestions);
    }, 900);
  };

  const handleResetChat = (e) => {
    e.stopPropagation();
    if (window.confirm("Are you sure you want to clear chat history?")) {
      if (typingTimerRef.current) clearInterval(typingTimerRef.current);
      setMessages(DEFAULT_GREETING);
      setCurrentQuestions(predefinedQuestions);
      setIsTyping(false);
      setIsTypewriting(false);
      localStorage.removeItem('tsil_chat_messages');
    }
  };

  if (!isOpen) {
    return (
      <div className="ai-chatbot-container closed" onClick={() => setIsOpen(true)}>
        <button className="ai-chat-toggle" aria-label="Open AI Assistant">
          <svg className="ai-toggle-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
          </svg>
          <span className="ai-toggle-badge" />
        </button>
      </div>
    );
  }

  return (
    <div className="ai-chatbot-container">
      <div className="ai-chatbot-header">
        <div className="ai-header-info">
          <span className="ai-header-dot" />
          <span className="ai-header-title">TSIL Assistant</span>
        </div>
        <div className="ai-header-actions">
          <button 
            className="ai-reset-btn" 
            onClick={handleResetChat} 
            title="Reset Chat"
            aria-label="Reset Chat History"
            disabled={isTyping || isTypewriting}
          >
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21.5 2v6h-6M21.34 15.57a10 10 0 1 1-.57-8.38l5.67-5.67"/>
            </svg>
          </button>
          <button className="ai-close-btn" onClick={(e) => { e.stopPropagation(); setIsOpen(false); }} aria-label="Close">
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>
      </div>
      
      <div className="ai-chat-messages">
        {messages.map((msg, index) => (
          <div key={msg.id || index} className={`ai-message ${msg.sender}`}>
            {msg.text}
          </div>
        ))}
        {isTyping && (
          <div className="ai-message ai typing-indicator">
            <span></span>
            <span></span>
            <span></span>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Hide suggestion questions while typing is in progress for cleaner interface */}
      {!isTyping && !isTypewriting && (
        <div className="ai-quick-questions">
          {currentQuestions.map((q, idx) => (
            <button 
              key={idx} 
              className="ai-quick-btn" 
              onClick={() => handleQuickQuestion(q)}
            >
              {q}
            </button>
          ))}
        </div>
      )}

      <form className="ai-chat-input-container" onSubmit={handleSend}>
        <input
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          placeholder="Ask me anything..."
          disabled={isTyping || isTypewriting}
        />
        <button 
          type="submit" 
          aria-label="Send" 
          className="ai-send-btn"
          disabled={!inputValue.trim() || isTyping || isTypewriting}
        >
          <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
            <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"></path>
          </svg>
        </button>
      </form>
    </div>
  );
}
